import React, { useEffect, useMemo, useRef, useState } from "react";
import { AGENTS, TAB_TARGETS, buildActions, scoreItem } from "../lib/agents";
import { api } from "../lib/api";
import {
  MagnifyingGlass, Command, ArrowRight, Brain, Sparkle, Wrench,
  CircuitryIcon, ArrowsClockwise, X,
} from "@phosphor-icons/react";

const TIER_ACCENT = {
  ORCHESTRATOR: "text-fuchsia-300",
  SME:          "text-emerald-300",
  UTILITY:      "text-sky-300",
  SYSTEM:       "text-amber-300",
};
const TIER_ICON = {
  ORCHESTRATOR: Brain, SME: Sparkle, UTILITY: Wrench, SYSTEM: CircuitryIcon,
};

export default function CommandPalette({ open, onClose, navigate, openProof, openSourcing }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(null); // {ok|error|busy: str}
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const actions = useMemo(
    () => buildActions({ api, navigate, openProof, openSourcing }),
    [navigate, openProof, openSourcing],
  );

  // Build a unified result list scored against `q`.
  const results = useMemo(() => {
    const items = [
      ...TAB_TARGETS.map(t => ({ kind: "nav", ...t })),
      ...actions.map(a => ({ kind: "action", ...a })),
      ...AGENTS.map(a => ({ kind: "agent", ...a })),
    ];
    if (!q.trim()) return items.slice(0, 24);
    const scored = items
      .map(i => ({ i, s: scoreItem(q, i) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 24)
      .map(x => x.i);
    return scored;
  }, [q, actions]);

  useEffect(() => {
    if (open) {
      setQ("");
      setStatus(null);
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Keyboard: Cmd/Ctrl+K opens, ESC closes, ↑↓ moves cursor, Enter executes
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (!open) document.dispatchEvent(new CustomEvent("procureo:openPalette"));
        else onClose();
      } else if (open) {
        if (e.key === "Escape") onClose();
        else if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
        else if (e.key === "ArrowUp")   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
        else if (e.key === "Enter")     { e.preventDefault(); executeItem(results[cursor]); }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, cursor, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  async function executeItem(item) {
    if (!item) return;
    setStatus({ busy: `Executing ${item.label || item.name}…` });
    try {
      if (item.kind === "nav") {
        navigate(item.tab);
        setStatus({ ok: `Switched to ${item.label}` });
        setTimeout(onClose, 350);
      } else if (item.kind === "action") {
        const match = item.pattern ? q.match(item.pattern) : null;
        const r = await item.run(match);
        if (r?.error) setStatus({ error: r.error });
        else { setStatus({ ok: r?.ok || "Done." }); setTimeout(onClose, 500); }
      } else if (item.kind === "agent") {
        setStatus({ ok: `Agent profile · ${item.name} · ${item.tier}` });
      }
    } catch (e) {
      setStatus({ error: e?.message || "Command failed." });
    }
  }

  // Also try pattern-matching arbitrary input (when no result selected)
  async function tryFreeform() {
    for (const a of actions) {
      if (a.pattern && a.pattern.test(q)) {
        await executeItem({ kind: "action", ...a });
        return;
      }
    }
  }

  if (!open) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl mx-4 bg-slate-950 border border-slate-700 rounded-md shadow-[0_30px_80px_-20px_rgba(15,23,42,0.4)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-900/60">
          <MagnifyingGlass weight="bold" className="text-slate-400 shrink-0" size={18} />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setCursor(0); }}
            onKeyDown={(e) => { if (e.key === "Enter" && results.length === 0) tryFreeform(); }}
            placeholder="Issue a command · 'should-cost PO-00103-VA' · 'risk for Bechtel' · 'go to opportunities'"
            className="flex-1 bg-transparent text-slate-50 placeholder:text-slate-500 outline-none font-mono text-sm"
          />
          <div className="hidden md:flex items-center gap-1 text-[10px] tracking-mono-label text-slate-500">
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm bg-slate-900">↑↓</kbd>
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm bg-slate-900">↵</kbd>
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm bg-slate-900">esc</kbd>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 p-1 rounded-sm" data-testid="command-palette-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {status && (
          <div
            data-testid="command-palette-status"
            className={`px-4 py-2 text-xs font-mono border-b border-slate-800 flex items-center gap-2 ${
              status.error ? "text-rose-300 bg-rose-500/10" :
              status.busy  ? "text-sky-300  bg-sky-500/10"  :
                              "text-emerald-300 bg-emerald-500/10"
            }`}
          >
            {status.busy && <ArrowsClockwise weight="bold" className="animate-spin" size={12} />}
            {status.ok || status.error || status.busy}
          </div>
        )}

        <ul className="max-h-[60vh] overflow-auto thin-scroll divide-y divide-slate-800/60" data-testid="command-palette-results">
          {results.length === 0 && (
            <li className="px-4 py-6 text-center text-xs text-slate-500 font-mono">
              No matches. Try: <span className="text-emerald-400">should-cost PO-00103-VA</span> or <span className="text-emerald-400">go to opportunities</span>.
            </li>
          )}
          {results.map((item, i) => {
            const active = i === cursor;
            const Icon = item.kind === "nav" ? ArrowRight
                       : item.kind === "action" ? Command
                       : TIER_ICON[item.tier] || Sparkle;
            const accent = item.kind === "nav" ? "text-slate-300"
                         : item.kind === "action" ? "text-fuchsia-300"
                         : TIER_ACCENT[item.tier] || "text-slate-300";
            return (
              <li
                key={item.id}
                data-testid={`palette-result-${item.id}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => executeItem(item)}
                className={`px-4 py-2.5 flex items-start gap-3 cursor-pointer transition-colors ${
                  active ? "bg-slate-800" : "hover:bg-slate-900"
                }`}
              >
                <Icon weight={item.kind === "agent" ? "duotone" : "bold"} className={`${accent} shrink-0 mt-0.5`} size={16} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-slate-50 text-sm truncate">{item.name || item.label}</span>
                    <span className="font-mono text-[10px] tracking-mono-label text-slate-500 shrink-0">
                      {item.kind === "agent" ? item.tier : item.kind === "nav" ? "NAV" : "ACTION"}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {item.summary || item.hint || (item.keywords ? `keywords: ${item.keywords.slice(0,4).join(", ")}` : "")}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <footer className="px-4 py-2 border-t border-slate-800 flex items-center justify-between text-[10px] font-mono text-slate-500">
          <span>{results.length} matches · {AGENTS.length} agents online</span>
          <span>Procureo Command Palette · Cmd / Ctrl + K</span>
        </footer>
      </div>
    </div>
  );
}
